import appStore from '@stores/appStore'
import { canShowRealtimeToast } from '@utils/functions'
import { notification } from 'antd'
import { observer } from 'mobx-react'
import { useCallback, useEffect, useMemo } from 'react'

import store from '../store'

import notificationService from './realtimeNotificationService'
import { ToastState, buildToast } from './toastConfig'
import { RealtimeNotificationData } from './types'

/**
 * Opens (or re-opens in place, same key) the toast for one notification. Expanding marks the
 * notification as read and pins the toast so it no longer closes by itself.
 */
export const showToast = (
  data: RealtimeNotificationData,
  key: string,
  state: ToastState
) => {
  const { kind, config } = buildToast(data, key, state, () => {
    if (!state.expanded && data.notificationId) {
      // Reading the message is enough to count as read. The expand must not fail with it, and
      // refresh() (not getSummary()) keeps the bell list and its unread count in step.
      void store
        .markAsRead(data.notificationId)
        .then(() => store.refresh())
        .catch(() => {})
    }
    showToast(data, key, { expanded: !state.expanded, pinned: true })
  })
  notification[kind](config)
}

const RealtimeNotification = () => {
  useEffect(() => {
    notificationService.setupRealtimeNotification().then(() => {
      notificationService.onNotification(receiveNotification)
    })
  }, [])

  // Shared with showNotificationError, which skips its own Xero toast only when this is true.
  const showPopup = useMemo(
    () => canShowRealtimeToast(),
    [appStore.currentUser, appStore.currentTenant, appStore.features]
  )

  const receiveNotification = useCallback((data: RealtimeNotificationData) => {
    store.refresh()
    if (!showPopup) {
      return
    }
    try {
      const key =
        data.notificationId ?? `${Date.now()}-${Math.random().toString(36)}`
      showToast(data, key, { expanded: false, pinned: false })
    } catch {}
  }, [])

  return <></>
}

export default observer(RealtimeNotification)
